import { useMemo, useState } from "react";
import { Close, MoreVert, OpenInNew } from "@mui/icons-material";
import {
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  brandPrimary70,
  darkBlue,
  lightBlueActive,
  lightBlueButton,
  lightSurface1,
  lightTextBody,
  lighterBlueActive,
  medBlue,
} from "constants/colors";
import styled from "styled-components/macro";
import { MACHEMETRY_URL } from "providers/MachemetryProvider";
import { lightBorder1 } from "constants/colors";
import { useBreakpoints } from "constants/constants";
import { SIGNALS } from "./signalValidationUtils";

export function SignalValidationDropdown({
  ge,
  lt,
  machemetryId,
  activeUrl,
  setActiveUrl,
  setIsViewingEntireShift,
  isViewingEntireShift,
}: {
  ge: number;
  lt: number;
  machemetryId: string | null;
  activeUrl: string;
  setActiveUrl: React.Dispatch<
    React.SetStateAction<{
      activeUrl: string;
      activeSignal: keyof typeof SIGNALS | "";
    }>
  >;
  setIsViewingEntireShift: React.Dispatch<
    React.SetStateAction<"segment" | "shift" | "all">
  >;
  isViewingEntireShift: "segment" | "shift" | "all";
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const handleClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const geSeconds = Math.floor(ge / 1000);
  const ltSeconds = Math.floor(lt / 1000);
  const debugUrls = useMemo(
    () =>
      Object.keys(SIGNALS).map(signal => ({
        name: signal,
        url: `${MACHEMETRY_URL}v2/measurements?s=${machemetryId}_${signal}&gte=${geSeconds}&lt=${ltSeconds}`,
      })),
    [geSeconds, ltSeconds, machemetryId]
  );
  const [pinnedItem, setPinnedItem] = useState<string | null>(null);
  const { isTabletOrLarger } = useBreakpoints();
  return (
    <DebugDropdownStyles
      onMouseLeave={() => {
        if (pinnedItem) {
          return;
        }
        setActiveUrl({
          activeUrl: "",
          activeSignal: "",
        });
      }}
    >
      <Tooltip title={"Signal validation"}>
        <IconButton
          onClick={handleClick}
          size="small"
          style={{ marginRight: -4 }}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>
      <Menu
        open={open}
        anchorEl={anchorEl}
        hideBackdrop={true}
        disableScrollLock={true}
      >
        <Paper className="menu-list">
          <IconButton
            style={{ position: "absolute", top: 0, right: 0, zIndex: 1 }}
            onClick={() => {
              setAnchorEl(null);
              setActiveUrl({
                activeUrl: "",
                activeSignal: "",
              });
            }}
          >
            <Close />
          </IconButton>
          <MenuList // allow interacting with the chart while the menu is open
          >
            <p style={{ margin: "0px 16px", color: lightTextBody }}>range:</p>
            <MenuItem style={{ pointerEvents: "none" }} disableRipple={true}>
              <ButtonGroup
                disableElevation
                variant="outlined"
                style={{ pointerEvents: "auto" }}
              >
                <Button
                  style={
                    isViewingEntireShift === "segment"
                      ? {
                          backgroundColor: lightBlueActive,
                          border: `1px solid ${brandPrimary70}`,
                        }
                      : {}
                  }
                  onClick={() => {
                    setIsViewingEntireShift("segment");
                  }}
                >
                  Segment
                </Button>
                <Button
                  style={
                    isViewingEntireShift === "shift"
                      ? {
                          backgroundColor: lightBlueActive,
                          border: `1px solid ${brandPrimary70}`,
                        }
                      : {}
                  }
                  onClick={() => {
                    setIsViewingEntireShift("shift");
                  }}
                >
                  Shift
                </Button>
                <Button
                  style={
                    isViewingEntireShift === "all"
                      ? {
                          backgroundColor: lightBlueActive,
                          border: `1px solid ${brandPrimary70}`,
                        }
                      : {}
                  }
                  onClick={() => {
                    setIsViewingEntireShift("all");
                  }}
                >
                  All
                </Button>
              </ButtonGroup>
            </MenuItem>
            <p style={{ margin: "0px 16px", color: lightTextBody }}>data:</p>
            {debugUrls.map(({ name, url }) => {
              return (
                <MenuItem
                  className={activeUrl === url ? "active" : ""}
                  key={url}
                  onMouseEnter={() => {
                    if (pinnedItem) {
                      return;
                    }
                    setActiveUrl({
                      activeUrl: url,
                      activeSignal: name as keyof typeof SIGNALS,
                    });
                  }}
                  onClick={() => {
                    if (!isTabletOrLarger) {
                      setAnchorEl(null);
                    }
                    setPinnedItem(prev => (prev === name ? null : name));
                    setActiveUrl({
                      activeUrl: url,
                      activeSignal: name as keyof typeof SIGNALS,
                    });
                  }}
                  style={{
                    width: "100%",
                    ...(pinnedItem === name
                      ? {
                          backgroundColor: "rgba(0,0,0,0.15)",
                          fontWeight: "bold",
                        }
                      : {}),
                  }}
                >
                  {name}&nbsp;
                  <a
                    href={url.replace("v2/", "")} // remove "v2/" from the link urls since the v2 endpoint requires authentication
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: lightBlueButton,
                      marginLeft: "auto",
                      transform: "translate(0px, 1px) scale(0.8)",
                    }}
                  >
                    <OpenInNew
                      style={{ transform: "translateX(4px) scale(0.9)" }}
                    />
                  </a>
                </MenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Menu>
    </DebugDropdownStyles>
  );
}
const DebugDropdownStyles = styled.div`
  cursor: pointer;
  position: absolute;
  top: 32px;
  right: 4px;
  .MuiPaper-root {
    max-height: unset;
  }
  .MuiSwitch-thumb {
    color: ${darkBlue};
  }
  .MuiSwitch-track {
    background-color: ${medBlue} !important;
  }
  .MuiButtonGroup-root {
    .MuiButtonBase-root {
      text-transform: none;
      background-color: ${lightSurface1};
      border-color: ${lightBorder1};
      &:hover {
        background-color: ${lighterBlueActive};
      }
    }
  }
  .menu-list {
  }
`;
